import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/i18n";

type I18nString = { fr: string; en: string; nl: string };
type I18nList = { fr: string[]; en: string[]; nl: string[] };

const pick = (v: unknown, lang: Lang): string => {
  if (v && typeof v === "object" && lang in (v as object)) {
    const s = (v as Record<string, unknown>)[lang];
    return typeof s === "string" ? s : "";
  }
  return "";
};
const pickList = (v: unknown, lang: Lang): string[] => {
  if (v && typeof v === "object" && lang in (v as object)) {
    const a = (v as Record<string, unknown>)[lang];
    return Array.isArray(a) ? (a.filter((x) => typeof x === "string") as string[]) : [];
  }
  return [];
};

export interface ServiceItem {
  id: string;
  num: string;
  slug: string;
  icon: string;
  title: string;
  intro: string;
  short_description: string;
  bullets: string[];
  tags: string[];
  modality: string;
  cta_label: string;
}

export function useServices(lang: Lang) {
  return useQuery({
    queryKey: ["public", "services", lang],
    queryFn: async (): Promise<ServiceItem[]> => {
      const { data, error } = await supabase
        .from("services")
        .select("*")
        .eq("status", "published")
        .order("display_order");
      if (error) throw error;
      return (data ?? []).map((s) => ({
        id: s.id,
        num: s.num,
        slug: s.slug,
        icon: s.icon,
        title: pick(s.title, lang),
        intro: pick(s.intro, lang),
        short_description: pick(s.short_description, lang),
        bullets: pickList(s.bullets, lang),
        tags: pickList(s.tags, lang),
        modality: pick(s.modality, lang),
        cta_label: pick(s.cta_label, lang),
      }));
    },
    staleTime: 60_000,
  });
}

export interface ReferenceItem {
  id: string;
  type: string;
  mission: string;
  duration: string;
  results: string[];
  tag: string;
  is_featured: boolean;
  cover_url: string | null;
  location: string | null;
}

export function useReferences(lang: Lang) {
  return useQuery({
    queryKey: ["public", "references", lang],
    queryFn: async (): Promise<ReferenceItem[]> => {
      const { data, error } = await supabase
        .from("references")
        .select("*")
        .eq("status", "published")
        .order("display_order");
      if (error) throw error;
      return (data ?? []).map((r) => ({
        id: r.id,
        type: pick(r.type, lang),
        mission: pick(r.mission, lang),
        duration: r.duration ?? "",
        results: pickList(r.results, lang),
        tag: pick(r.tag, lang),
        is_featured: r.is_featured,
        cover_url: r.cover_url,
        location: r.location,
      }));
    },
    staleTime: 60_000,
  });
}

export interface FaqItem {
  id: string;
  q: string;
  a: string;
  category: string;
}

export function useFaqs(lang: Lang) {
  return useQuery({
    queryKey: ["public", "faqs", lang],
    queryFn: async (): Promise<FaqItem[]> => {
      const { data, error } = await supabase
        .from("faqs")
        .select("*")
        .eq("status", "published")
        .order("display_order");
      if (error) throw error;
      return (data ?? []).map((f) => ({
        id: f.id,
        q: pick(f.question, lang),
        a: pick(f.answer, lang),
        category: f.category,
      }));
    },
    staleTime: 60_000,
  });
}

export interface StatItem {
  id: string;
  value: string;
  label: string;
}

export function useStats(lang: Lang) {
  return useQuery({
    queryKey: ["public", "stats", lang],
    queryFn: async (): Promise<StatItem[]> => {
      const { data, error } = await supabase
        .from("stats")
        .select("*")
        .eq("is_visible", true)
        .order("display_order");
      if (error) throw error;
      return (data ?? []).map((s) => ({
        id: s.id,
        value: s.value,
        label: pick(s.label, lang),
      }));
    },
    staleTime: 60_000,
  });
}

export type { I18nString, I18nList };
